import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Select from "../../../components/ui/Select";
import Button from "../../../components/ui/Button";
import Image from "../../../components/AppImage";
import ImageSourceDropdown from "./ImageSourceDropdown";
import { processImagesWithOverlay } from "../../../utils/imageOverlay";

const RoofInspectionSection = ({
  roofStatus,
  rejectionReason,
  roofInspectionPhotos,
  onRoofStatusChange,
  onReasonChange,
  onRoofInspectionPhotosChange,
  overlayMeta,
  capturedLocation,
  errors,
}) => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isProcessingPhotos, setIsProcessingPhotos] = useState(false);
  const rejectionReasons = [
    { value: "insufficient_space", label: "Insufficient Roof Space" },
    { value: "structural_issues", label: "Structural Issues" },
    { value: "shading_problems", label: "Excessive Shading" },
    { value: "orientation_unsuitable", label: "Unsuitable Orientation" },
    { value: "access_restricted", label: "Restricted Access" },
    { value: "other", label: "Other Reasons" },
  ];

  const handleRoofInspectionPhotosSelect = async (files) => {
    if (!files?.length) return;
    setIsProcessingPhotos(true);
    try {
      const processedFiles = await processImagesWithOverlay(files, overlayMeta, capturedLocation);
      const newPhotos = [...roofInspectionPhotos, ...processedFiles].slice(0, 2);
      onRoofInspectionPhotosChange(newPhotos);
    } finally {
      setIsProcessingPhotos(false);
    }
  };

  const removeRoofPhoto = (index) => {
    const newPhotos = roofInspectionPhotos?.filter((_, i) => i !== index);
    onRoofInspectionPhotosChange(newPhotos);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-4 md:p-6 lg:p-8">
      <div className="flex items-center gap-3 mb-4 md:mb-6">
        <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
          <span className="text-lg font-semibold text-secondary">2</span>
        </div>
        <h2 className="text-xl md:text-2xl font-semibold text-foreground">
          Roof Inspection
        </h2>
      </div>
      <div className="space-y-4 md:space-y-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Roof Condition Assessment
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
            <button
              type="button"
              onClick={() => onRoofStatusChange("acceptable")}
              className={`flex items-center justify-center gap-3 p-4 md:p-5 rounded-lg border-2 transition-all ${
                roofStatus === "acceptable"
                  ? "border-success bg-success/10 text-success"
                  : "border-border bg-background text-muted-foreground hover:border-success/50"
              }`}
            >
              <Icon name="CheckCircle2" size={24} />
              <span className="font-medium text-base md:text-lg">
                Acceptable
              </span>
            </button>

            <button
              type="button"
              onClick={() => onRoofStatusChange("not_acceptable")}
              className={`flex items-center justify-center gap-3 p-4 md:p-5 rounded-lg border-2 transition-all ${
                roofStatus === "not_acceptable"
                  ? "border-error bg-error/10 text-error"
                  : "border-border bg-background text-muted-foreground hover:border-error/50"
              }`}
            >
              <Icon name="XCircle" size={24} />
              <span className="font-medium text-base md:text-lg">
                Not Acceptable
              </span>
            </button>
          </div>
          {errors?.roofStatus && (
            <p className="text-sm text-error mt-2">{errors?.roofStatus}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Roof Inspection Photos
          </label>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-muted-foreground">
              Capture photos of the roof area for inspection (Maximum 2 photos)
            </p>
            <span className="text-sm font-medium text-foreground">
              {roofInspectionPhotos?.length}/2
            </span>
          </div>

          {roofInspectionPhotos?.length < 2 && (
            <div className="mb-4">
              <ImageSourceDropdown
                idPrefix="roof-inspection-photos-upload"
                triggerLabel="Add Roof Inspection Photos"
                triggerSubLabel="Include roof structure, condition, and installation area"
                multiple
                processing={isProcessingPhotos}
                disabled={roofInspectionPhotos?.length >= 2}
                onFileSelect={handleRoofInspectionPhotosSelect}
              />
            </div>
          )}

          {roofInspectionPhotos?.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              {roofInspectionPhotos?.map((photo, index) => {
                // Handle both File objects (new uploads) and string URLs (existing photos)
                const photoUrl = photo instanceof File 
                  ? URL.createObjectURL(photo) 
                  : photo;
                
                return (
                <div
                  key={index}
                  className="relative group rounded-lg overflow-hidden border border-border bg-muted"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <Image
                      src={photoUrl}
                      alt={`Roof inspection photo ${index + 1}`}
                      className="w-full h-full object-contain bg-muted"
                    />
                  </div>

                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      iconName="Eye"
                      onClick={() => setSelectedPhoto(photo)}
                    >
                      View
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      iconName="Trash2"
                      onClick={() => removeRoofPhoto(index)}
                    >
                      Remove
                    </Button>
                  </div>

                  <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                    Photo {index + 1}
                  </div>
                </div>
                );
              })}
            </div>
          )}

          {errors?.roofInspectionPhotos && (
            <p className="text-sm text-error mt-2">
              {errors?.roofInspectionPhotos}
            </p>
          )}
        </div>

        {roofStatus === "not_acceptable" && (
          <div className="space-y-4 p-4 bg-error/5 rounded-lg border border-error/20">
            <Select
              label="Rejection Reason"
              placeholder="Select reason for rejection"
              options={rejectionReasons}
              value={rejectionReason}
              onChange={onReasonChange}
              error={errors?.rejectionReason}
              searchable
            />
          </div>
        )}
      </div>

      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black/80 z-[2000] flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="relative max-w-4xl w-full">
            <Button
              variant="secondary"
              size="icon"
              iconName="X"
              onClick={() => setSelectedPhoto(null)}
              className="absolute -top-12 right-0"
            />
            <Image
              src={selectedPhoto instanceof File 
                ? URL.createObjectURL(selectedPhoto) 
                : selectedPhoto}
              alt="Full size roof inspection photo"
              className="w-full h-auto rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default RoofInspectionSection;
